import { useEffect, useState } from 'react';

import { pixelThumb, rawUrl, renderSize, renderThumbUrl, thumbKind, waveformPath } from '../../lib/thumbnails.js';
import { assetKind, type AssetKind } from '../../state/project-files.js';
import { Icon, type IconName } from '../ui/icons.js';
import { FontPreview } from './FontPreview.js';

export const ASSET_ICON: Record<AssetKind, IconName> = {
  folder: 'folder',
  scene: 'scene',
  model: 'model',
  pixel: 'pixel',
  sound: 'audio',
  video: 'camera',
  prefab: 'prefab',
  material: 'material',
  script: 'script',
  data: 'data',
  ui: 'ui',
  mesh: 'cube',
  texture: 'image',
  audio: 'audio',
  font: 'file',
  project: 'logo',
  doc: 'explorer',
  file: 'file',
};

/**
 * Preview of one project file at `size` px: image, pixel art, engine render
 * (models, materials, prefabs), waveform (audio), font sample, else the
 * type icon. Falls back to the icon whenever a preview cannot be made.
 */
export function AssetThumb({
  projectId,
  path,
  version,
  size,
  folder = false,
  className = '',
}: {
  projectId: number;
  path: string;
  /** Modification time (or any token that changes with the file). */
  version: string;
  size: number;
  folder?: boolean;
  className?: string;
}) {
  const name = path.slice(path.lastIndexOf('/') + 1);
  const kind = folder ? 'folder' : assetKind({ name, kind: 'file' });
  const thumb = folder ? 'none' : thumbKind(path);
  const [failed, setFailed] = useState(false);
  const [data, setData] = useState<string | null>(null);

  useEffect(() => {
    setFailed(false);
    setData(null);
    if (thumb !== 'pixel' && thumb !== 'waveform') return;
    let cancelled = false;
    const job = thumb === 'pixel' ? pixelThumb(projectId, path, version) : waveformPath(projectId, path, version);
    void job.then((result) => {
      if (cancelled) return;
      if (result) setData(result);
      else setFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, path, version, thumb]);

  const style = { width: size, height: size };
  const cls = `asset-icon asset-thumb kind-${kind}${className ? ` ${className}` : ''}`;
  const icon = (
    <span className={cls} style={style}>
      <Icon name={ASSET_ICON[kind]} size={Math.max(14, Math.round(size * 0.48))} />
    </span>
  );

  if (kind === 'font' && size >= 32) return <FontPreview className={cls} projectId={projectId} path={path} />;
  if (failed || thumb === 'none') return icon;

  if (thumb === 'image' || thumb === 'render') {
    const src = thumb === 'image' ? rawUrl(projectId, path, version) : renderThumbUrl(projectId, path, renderSize(size), version);
    return (
      <span className={`${cls} has-preview${thumb === 'image' ? ' is-image' : ''}`} style={style}>
        <img src={src} alt="" loading="lazy" draggable={false} onError={() => setFailed(true)} />
      </span>
    );
  }
  if (!data) return icon;
  if (thumb === 'pixel') {
    return (
      <span className={`${cls} has-preview is-image is-pixel`} style={style}>
        <img src={data} alt="" draggable={false} />
      </span>
    );
  }
  return (
    <span className={`${cls} has-preview is-waveform`} style={style}>
      <svg viewBox="0 0 64 2" preserveAspectRatio="none" aria-hidden="true">
        <path d={data} />
      </svg>
    </span>
  );
}
